import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useFeather } from '../shell/useFeather';
import { confirmDialog } from '../shell/dialogs';
import { AffilmasterApiError } from '../api/affilmaster';
import { fmtDen } from './affilmaster-format';
import { Button } from '../components/arc/button/button';
import { ArcPanel } from '../components/arc/primitives/arc-primitives';

export interface AffilmasterNoteItem {
  id: number;
  body: string;
  source: string;
  notedAt: string | null;
  createdBy: string | null;
}

interface AffilmasterNotesSectionProps {
  resourceKey: string;
  loadNotes: () => Promise<AffilmasterNoteItem[]>;
  addNote: (body: string) => Promise<unknown>;
  updateNote: (noteId: number, body: string) => Promise<unknown>;
  deleteNote: (noteId: number) => Promise<unknown>;
  placeholder: string;
  emptyText: string;
}

function countLabel(count: number): string {
  if (count === 1) return '1 poznámka';
  if (count >= 2 && count <= 4) return `${count} poznámky`;
  return `${count} poznámek`;
}

export function AffilmasterNotesSection({
  resourceKey,
  loadNotes,
  addNote,
  updateNote,
  deleteNote,
  placeholder,
  emptyText,
}: AffilmasterNotesSectionProps) {
  const { me } = useAuth();
  const canManage = !!me?.permissionKeys?.includes('affilmaster:manage');
  const [rows, setRows] = useState<AffilmasterNoteItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [draft, setDraft] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [busy, setBusy] = useState(false);
  const loadedResource = useRef(resourceKey);

  useEffect(() => {
    let cancelled = false;
    if (loadedResource.current !== resourceKey) {
      loadedResource.current = resourceKey;
      setRows(null);
      setDraft('');
      setEditId(null);
      setEditText('');
    }
    loadNotes()
      .then((data) => {
        if (!cancelled) {
          setRows(data);
          setError(null);
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof AffilmasterApiError ? reason.message : 'Načtení poznámek selhalo');
      });
    return () => { cancelled = true; };
  }, [resourceKey, version]);

  useFeather([rows, editId, canManage, busy]);

  const save = async (action: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    try {
      await action();
      after?.();
      setVersion((value) => value + 1);
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Uložení selhalo');
    } finally {
      setBusy(false);
    }
  };

  if (!rows && !error) {
    return (
      <div className="p1-notes-loading" aria-label="Načítám poznámky">
        <span className="spinner-border spinner-border-sm" />
        Načítám poznámky…
      </div>
    );
  }

  if (!rows) return <div className="alert alert-danger mb-0">{error}</div>;

  return (
    <section className="p1-arc-scope p1-notes" aria-label="Poznámky">
      {error && <div className="alert alert-danger mb-0">{error}</div>}

      {canManage && (
        <ArcPanel className="p1-notes-composer">
          <div className="p1-notes-composer-head">
            <span className="p1-notes-composer-icon" aria-hidden="true"><i data-feather="edit-3" /></span>
            <div>
              <strong>Přidat poznámku</strong>
              <span>Interní informace dostupná týmu v Platform 1</span>
            </div>
          </div>
          <textarea
            className="form-control"
            rows={4}
            placeholder={placeholder}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="p1-notes-composer-actions">
            <span className={draft.trim().length > 60_000 ? 'is-over-limit' : ''}>{draft.trim().length.toLocaleString('cs-CZ')} / 60 000</span>
            <Button
              type="button"
              size="sm"
              disabled={busy || draft.trim().length === 0 || draft.trim().length > 60_000}
              onClick={() => void save(() => addNote(draft.trim()), () => setDraft(''))}
            >
              <i data-feather="plus" />
              Přidat poznámku
            </Button>
          </div>
        </ArcPanel>
      )}

      <ArcPanel className="p1-notes-feed">
        <div className="p1-notes-feed-head">
          <strong>{countLabel(rows.length)}</strong>
          {rows.length > 0 && <span>Nejnovější nahoře</span>}
        </div>

        {rows.length === 0 ? (
          <div className="p1-notes-empty">
            <span aria-hidden="true"><i data-feather="message-square" /></span>
            <strong>Zatím bez poznámek</strong>
            <p>{emptyText}</p>
          </div>
        ) : (
          <ol className="p1-notes-list">
            {rows.map((note) => (
              <li className="p1-note" key={note.id}>
                <div className="p1-note-marker" aria-hidden="true"><i data-feather="file-text" /></div>
                <div className="p1-note-content">
                  <header className="p1-note-head">
                    <div className="p1-note-meta">
                      <time dateTime={note.notedAt ?? undefined}>{fmtDen(note.notedAt)}</time>
                      <span>{note.source === 'zoho' ? 'Zoho CRM' : 'Platform 1'}</span>
                    </div>
                    {canManage && editId !== note.id && (
                      <div className="p1-note-actions">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          title="Upravit poznámku"
                          aria-label="Upravit poznámku"
                          onClick={() => { setEditId(note.id); setEditText(note.body); }}
                        >
                          <i data-feather="edit-2" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="is-delete"
                          title="Smazat poznámku"
                          aria-label="Smazat poznámku"
                          disabled={busy}
                          onClick={() => {
                            void confirmDialog({ title: 'Smazat poznámku?', message: 'Poznámka bude trvale odstraněna.', confirmLabel: 'Smazat', danger: true })
                              .then((confirmed) => { if (confirmed) void save(() => deleteNote(note.id)); });
                          }}
                        >
                          <i data-feather="trash-2" />
                        </Button>
                      </div>
                    )}
                  </header>

                  {editId === note.id ? (
                    <div className="p1-note-edit">
                      <textarea
                        className="form-control"
                        rows={Math.min(14, Math.max(4, editText.split('\n').length + 1))}
                        value={editText}
                        onChange={(event) => setEditText(event.target.value)}
                      />
                      <div className="p1-note-edit-actions">
                        <Button
                          type="button"
                          size="sm"
                          disabled={busy || editText.trim().length === 0 || editText.trim().length > 60_000}
                          onClick={() => void save(() => updateNote(note.id, editText.trim()), () => setEditId(null))}
                        >
                          Uložit změny
                        </Button>
                        <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => setEditId(null)}>Zrušit</Button>
                      </div>
                    </div>
                  ) : (
                    <p className="p1-note-body">{note.body}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </ArcPanel>
    </section>
  );
}
